import React from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useNodeResize } from './useNodeResize';
import { Image } from 'lucide-react';

interface ImageNodeDataProps {
  imageUrl?: string;
  thumbnailUrl?: string;
  nodeWidth?: number;
  onWidthChange?: (id: string, width: number) => void;
}

export function ImageNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as ImageNodeDataProps;
  const imageUrl = data.imageUrl || data.thumbnailUrl;

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: data.nodeWidth as number | undefined,
    minWidth: 180,
    maxWidth: 600,
    onWidthChange: (w) => data.onWidthChange?.(id, w),
  });

  return (
    <div className={`bg-white rounded-lg border-2 shadow-md overflow-hidden min-w-[180px] relative ${selected ? 'border-primary-500' : 'border-gray-200'}`} style={nodeWidth ? { width: nodeWidth } : undefined}>
      <div className="px-3 py-2 bg-secondary-50 border-b border-secondary-100 flex items-center gap-2">
        <Image className="w-4 h-4 text-secondary-600" />
        <span className="text-xs font-medium text-secondary-700">Image</span>
      </div>
      {imageUrl ? (
        <div className="p-2">
          <img
            src={imageUrl}
            alt="Generated"
            className="w-full h-auto rounded-md max-h-[200px] object-cover"
          />
        </div>
      ) : (
        <div className="p-4 flex items-center justify-center h-[120px] bg-gray-50">
          <span className="text-sm text-gray-400">No image</span>
        </div>
      )}
      <Handle type="source" position={Position.Right} className="!bg-secondary-500 !w-3 !h-3" />
      <Handle type="target" position={Position.Left} className="!bg-secondary-500 !w-3 !h-3" />
      <div {...resizeHandleProps} />
    </div>
  );
}
